import { normalizeCpfCnpj } from 'app/utils/normalizer';
import { stripTaxpayerId } from 'app/helpers';
import { useTheme } from '@mui/material';

import { useAuthContext, usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import { TProducts, useQualificationsContext } from 'modules/products/context';
import { EQualificationStatus } from 'modules/products/context/QualificationsProvider/qualifications.interfaces';
import { TQualificationStatusOption } from 'modules/products/pages/interfaces';
import {
  useApplicationsService,
  useProductsService,
} from 'modules/products/services/hooks';
import {
  TApplicationResponse,
  TQualificationRequestApplication,
  IList,
} from 'modules/products/services/hooks/interfaces';
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  CustomTableSelect,
  BoxSection,
  ButtonPrimary,
  Heading4,
  Spinner,
} from 'app/components';
import { TextM, TextS } from 'app/components/typography/body';
import {
  formatDateForBackendString,
  formatDateFromBackend,
} from 'app/helpers/dateHelpers';
import { formatDateTimePtBr } from 'utils/helpers';
import {
  ButtonContainer,
  Container,
  EmptyContainer,
  TableArea,
  TableWrapper,
} from './styles';
import { CopyIcon } from 'app/components/Icons';
import { EmptyApplications } from 'app/components/Icons/svgs';
import {
  handleGetStaticStatusList,
  handleStatusListPermissions,
} from 'modules/products/helpers/statusList';
import { filterVisibleApplications } from 'modules/products/helpers/productVisibility';
import { useQualificationChangeContext } from 'modules/products/context/QualificationChangeProvider/QualificationChangeProvider';
import { Table } from 'app/components';
import {
  TableBodyCellContainer,
  TableHeadCellContainer,
} from 'app/styles/components/Table';
import {
  useQueryParam,
  NumberParam,
  withDefault,
  JsonParam,
} from 'use-query-params';
import ApplicationsFilters, {
  TFilters,
} from 'modules/common/ApplicationsFilters/ApplicationsFilters';
import { initalFilters } from 'modules/common/ApplicationsFilters/filters';
import { ApplicationDetailsSideSheet } from '../ApplicationDetailsSideSheet';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import {
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { TFilterItems } from 'app/components/SearchFilter/SearchFilter';
import { useOriginatorContext } from 'modules/common/OriginatorProvider';
import { useAppContext } from 'app/context/AppContextProvider';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { ProductType } from 'modules/products/constants/enums';
import { TypeProductEmission } from 'modules/products/context/ProductsProvider/products.interfaces';
import EyeIcon from 'app/components/Icons/EyeIcon';
import {
  EApplicationStatus,
  EApplicationStatusDescription,
} from 'modules/products/services/hooks/useApplicationsService';
import ApplicationsSearchFilters, {
  APPLICATION_SEARCH_FILTER_ITEMS,
} from 'modules/common/components/ApplicationsSearchFilters/ApplicationsSearchFilters';

const tableHeader = [
  {
    id: '1',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">ID da solicitação</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '2',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">Tipo</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '3',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">CCB</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '4',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ textAlign: 'center' }}>
          Dt. Criação
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '5',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ textAlign: 'center' }}>
          Originador
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '6',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ textAlign: 'center' }}>
          CPF/CNPJ do tomador
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '7',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ textAlign: 'center' }}>
          Produto
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '8',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ width: '150px' }}>
          Dt. Desembolso
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '9',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold" style={{ textAlign: 'center' }}>
          Status
        </TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '10',
    content: (
      <TableHeadCellContainer>
        <div />
      </TableHeadCellContainer>
    ),
  },
];

export type TApplicationData = {
  productData?: TProducts;
  qualificationData?: TQualificationRequestApplication;
  statusApproval: TQualificationStatusOption[];
  borrowerDetails?: {
    name?: string;
    taxpayer_id?: string;
  };
} & TApplicationResponse;

export const PendingApplicationsStatus = [
  EApplicationStatus.PENDING_SIGNATURE,
  EApplicationStatus.PENDING_GUARANTEE,
  EApplicationStatus.PENDING_QUALIFICATION,
  EApplicationStatus.PENDING_CESSION_QUALIFICATION,
  EApplicationStatus.AWAITING_APPROVAL_DISBURSEMENT,
];

type ApplicationContentProps = {
  pending?: boolean;
};
const ApplicationContent = ({ pending }: ApplicationContentProps) => {
  const theme = useTheme();
  const {
    state: { borrowerStatusOptions },
  } = useQualificationsContext();
  const {
    handleUpdateQualificationStatus,
    handleUpdateQualificationStatusLoading,
  } = useQualificationChangeContext();
  const { showSnackbarCopy } = useSnackbar();
  const { originators } = useOriginatorContext();
  const [searchInputValue, setSearchInputValue] = useState('');
  const { getBusiness } = useCustomerService();

  // HK EXCLUIR ESSE CONTEXTO LEGADO E PASSAR CHAMDAS PARA O CONTEXTO CORRETO: usePersonContext
  const { getPerson } = usePersonService();

  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<TFilters>(initalFilters as TFilters);
  const [selectedFilter, setSelectedFilter] = useQueryParam<TFilterItems>(
    'filter',
    withDefault(JsonParam, APPLICATION_SEARCH_FILTER_ITEMS[0]),
  );
  const { setSelectedOriginator } = useOriginatorContext();
  const { getQualificationStatusOptions, getQualificationRequestApplication } =
    useApplicationsService();
  const { getApplications, getApplication, loadings } =
    useApplicationsService();
  const { getProduct } = useProductsService();
  const [isLoading, setIsLoading] = useState(false);
  const [linesPerPage, setLinesPerPage] = useQueryParam(
    'size',
    withDefault(NumberParam, 10),
  );
  const [totalItemsQty, setTotalItemsQty] = useState(0);
  const [currentPage, setCurrentPage] = useQueryParam(
    'page',
    withDefault(NumberParam, 0),
  );
  const [applicationState, setApplicationState] = useState<
    TApplicationData[] | undefined
  >([]);
  const [applicationDetail, setApplicationDetail] =
    useState<TApplicationData | null>(null);
  const { isProfileAuthorized } = usePermify();
  const { userInfo } = useAuthContext();
  const { is_issuer_app } = useAppContext();
  const hasActiveSearch = searchInputValue.trim().length > 0;

  const handleSearchInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = evt.target;

    if (selectedFilter?.parameterName === 'sequential_id') {
      const onlyNumbers = value.replace(/\D/g, '').slice(0, 10);
      setSearchInputValue(() => onlyNumbers);
      return;
    }

    setSearchInputValue(() => value);
  };

  const getStatusFilter = () => {
    const hasCustomStatusFilter =
      Array.isArray(filters.status) &&
      filters.status.length > 0 &&
      filters.status.length !== initalFilters.status.length;

    if (pending) {
      return hasCustomStatusFilter ? filters.status : PendingApplicationsStatus;
    }
    return filters.status &&
      filters.status.length === initalFilters.status.length
      ? undefined
      : filters.status;
  };

  const handleGetApplications = async (
    page: number,
    itemsPerPage?: number,
    searchApplicationId?: string,
  ) => {
    const normalizeSearch = (value?: string) =>
      typeof value === 'string' ? value.trim() : '';
    const effectiveSearchValue =
      searchApplicationId !== undefined
        ? normalizeSearch(searchApplicationId)
        : normalizeSearch(searchInputValue);

    setIsLoading(true);
    setApplicationState([]);
    try {
      const applicationData: IList<TApplicationResponse> | null =
        effectiveSearchValue.length > 0 &&
        selectedFilter?.parameterName === 'id'
          ? await getApplication(effectiveSearchValue).then(
              (application) =>
                ({
                  total_elements: 1,
                  content: [application],
                  has_next: false,
                  total_pages: 1,
                } as IList<TApplicationResponse>),
            )
          : await getApplications(
              page,
              itemsPerPage,
              {
                product_id: filters?.productId,
                borrower_id: filters?.borrower,
                created_date_from: formatDateForBackendString(
                  filters?.minCreatedAtDate,
                ),
                created_date_to: formatDateForBackendString(
                  filters?.maxCreatedAtDate,
                ),
                disbursement_date_from: formatDateForBackendString(
                  filters?.minDisbursementDate,
                ),
                disbursement_date_to: formatDateForBackendString(
                  filters?.maxDisbursementDate,
                ),
                disbursement_processed_date_from: formatDateForBackendString(
                  filters.minDisbursementProcessedAtDate,
                ),
                disbursement_processed_date_to: formatDateForBackendString(
                  filters.maxDisbursementProcessedAtDate,
                ),
                funding_id: filters?.fundingId,
                taxpayer_id: stripTaxpayerId(filters?.taxpayerId ?? ''),
                ...(selectedFilter?.parameterName &&
                effectiveSearchValue.length > 0
                  ? { [selectedFilter.parameterName]: effectiveSearchValue }
                  : {}),
                ...(filters?.originator &&
                  filters?.originator.length > 0 && {
                    originator_id: filters?.originator,
                  }),
              },
              getStatusFilter(),
            );
      setTotalItemsQty(applicationData?.total_elements || 0);

      if (
        applicationData?.content !== undefined &&
        Array.isArray(applicationData?.content)
      ) {
        const statusApprovalCache = new Map<
          string,
          TQualificationStatusOption[]
        >();
        const productDataCache = new Map<string, TProducts | undefined>();
        const borrowerDetailsCache = new Map<
          string,
          TRegisterBusinessDataRequest | TRegisterPersonDataRequest
        >();
        const borrowerDetailsPromiseCache = new Map<
          string,
          Promise<TRegisterBusinessDataRequest | TRegisterPersonDataRequest>
        >();

        const applications = applicationData.content;

        const uniqueStatusKeys = Array.from(
          new Set(
            applications
              .filter((application) =>
                [
                  EApplicationStatus.PENDING_QUALIFICATION,
                  EApplicationStatus.PENDING_CESSION_QUALIFICATION,
                ].includes(application.status as EApplicationStatus),
              )
              .map((application) => {
                const originator = is_issuer_app
                  ? application?.originator?.id
                  : undefined;
                return `${application.product.id}:${originator || 'default'}:${
                  application.status
                }`;
              }),
          ),
        );

        await Promise.all(
          uniqueStatusKeys.map(async (key) => {
            const [productIdFromKey, originatorFromKey, applicationStatus] =
              key.split(':');
            const originatorId =
              originatorFromKey === 'default' ? undefined : originatorFromKey;

            const response =
              (await getQualificationStatusOptions(
                productIdFromKey,
                originatorId,
                applicationStatus as EApplicationStatus,
              )) || [];

            statusApprovalCache.set(key, response);
          }),
        );

        const uniqueProductIds = Array.from(
          new Set(applications.map((application) => application.product.id)),
        );

        await Promise.all(
          uniqueProductIds.map(async (productId) => {
            const productResponse = await getProduct(productId);
            productDataCache.set(productId, productResponse?.data);
          }),
        );

        const applicationDataWithProduct = await Promise.all<TApplicationData>(
          applications.map(async (application) => {
            const originator = is_issuer_app
              ? application?.originator?.id
              : undefined;
            const productId = application.product.id;

            const statusApprovalCacheKey = `${productId}:${
              originator || 'default'
            }:${application.status}`;

            const statusApproval =
              statusApprovalCache.get(statusApprovalCacheKey) || [];

            const qualificationDataArr =
              await getQualificationRequestApplication(
                productId,
                0,
                application.borrower.id,
                application.id,
                originator,
                application.status as EApplicationStatus,
              );

            let productData = productDataCache.get(productId);

            try {
              if (!productData) {
                const productResponse = await getProduct(productId);
                productData = productResponse?.data;
                productDataCache.set(productId, productData);
              }

              const borrowerCacheKey = `${application.borrower.id}:${
                originator || 'default'
              }`;

              let borrowerDetails = borrowerDetailsCache.get(borrowerCacheKey);

              if (!borrowerDetails && productData) {
                let fetchPromise =
                  borrowerDetailsPromiseCache.get(borrowerCacheKey);

                if (!fetchPromise) {
                  fetchPromise =
                    productData?.borrower_type === 'PERSON'
                      ? (getPerson(
                          application.borrower.id,
                          false,
                          originator,
                        ) as Promise<TRegisterPersonDataRequest>)
                      : (getBusiness(
                          application.borrower.id,
                          false,
                          originator,
                        ) as Promise<TRegisterBusinessDataRequest>);

                  borrowerDetailsPromiseCache.set(
                    borrowerCacheKey,
                    fetchPromise,
                  );
                }

                borrowerDetails = await fetchPromise;
                borrowerDetailsCache.set(borrowerCacheKey, borrowerDetails);
              }

              return {
                ...application,
                qualificationData: qualificationDataArr
                  ? qualificationDataArr?.content?.[0]
                  : undefined,
                statusApproval,
                borrowerDetails:
                  productData?.borrower_type === 'PERSON'
                    ? {
                        name: (
                          borrowerDetails as
                            | TRegisterPersonDataRequest
                            | undefined
                        )?.full_name,
                        taxpayer_id: (
                          borrowerDetails as
                            | TRegisterPersonDataRequest
                            | undefined
                        )?.taxpayer_id,
                      }
                    : {
                        name: (
                          borrowerDetails as
                            | TRegisterBusinessDataRequest
                            | undefined
                        )?.legal_name,
                        taxpayer_id: (
                          borrowerDetails as
                            | TRegisterBusinessDataRequest
                            | undefined
                        )?.taxpayer_id,
                      },
                productData,
              };
            } catch {
              return {
                ...application,
                qualificationData: qualificationDataArr
                  ? qualificationDataArr?.content?.[0]
                  : undefined,
                statusApproval,
                productData,
              };
            }
          }),
        );
        setApplicationState((state) =>
          state?.concat(
            filterVisibleApplications(applicationDataWithProduct).filter(
              (content) => {
                const inx = state?.findIndex(
                  (stateContent) => stateContent?.id === content?.id,
                );
                return inx < 0;
              },
            ),
          ),
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedOriginator(null);
    setTimeout(() => {
      if (currentPage === 0) {
        handleGetApplications(0, linesPerPage);
      } else {
        setCurrentPage(0);
      }
    });
  }, [filters, pending]);

  const handleOpenApplicationDetail = (
    application: TApplicationData | undefined,
  ) => {
    if (application) setApplicationDetail(application);
  };

  const handleCloseApplicationDetail = () => {
    setApplicationDetail(null);
  };

  const handleUpdateQualificationStatusAction = (
    application: TApplicationData | null,
    identifier: string,
  ) => {
    handleUpdateQualificationStatus(
      application as TApplicationResponse,
      identifier,
      application?.qualificationData,
    ).then((retn) => {
      if (retn) {
        setApplicationState((state) =>
          state?.map((applicationFromState) => {
            if (applicationFromState?.id === application?.id) {
              if (retn.isQualification) {
                return {
                  ...applicationFromState,
                  qualificationData:
                    retn.newStatus as TQualificationRequestApplication,
                };
              }
              return {
                ...applicationFromState,
                status: retn.newStatus as string,
              };
            }
            return applicationFromState;
          }),
        );
        if (applicationDetail) {
          setApplicationDetail(() => {
            const updatedApplicationDetail = { ...applicationDetail };
            if (retn.isQualification) {
              return {
                ...updatedApplicationDetail,
                qualificationData:
                  retn.newStatus as TQualificationRequestApplication,
              };
            }
            return {
              ...updatedApplicationDetail,
              status: retn.newStatus as string,
            };
          });
        }
      }
    });
  };

  const handleSearch = () => {
    const normalizedSearchValue =
      typeof searchInputValue === 'string' ? searchInputValue.trim() : '';

    if (!selectedFilter?.parameterName || normalizedSearchValue.length === 0) {
      return;
    }

    setCurrentPage(0);
    setLinesPerPage(10);
    handleGetApplications(0, linesPerPage, normalizedSearchValue);
  };

  const getApplicationStatusValue = useCallback(
    (application: TApplicationData) => {
      if (
        [
          EApplicationStatus.PENDING_QUALIFICATION,
          EApplicationStatus.PENDING_CESSION_QUALIFICATION,
        ].includes(application.status as EApplicationStatus)
      ) {
        return (
          application.qualificationData?.status ||
          EQualificationStatus.PENDING_RANDOM
        );
      }

      return application.status;
    },
    [],
  );

  const getStatusList = useCallback((application: TApplicationData) => {
    const { statusApproval } = application;
    const isPendingQualification = [
      EApplicationStatus.PENDING_QUALIFICATION,
      EApplicationStatus.PENDING_CESSION_QUALIFICATION,
    ].includes(application.status as EApplicationStatus);

    if (isPendingQualification && statusApproval?.length > 0) {
      const options = [
        {
          label: 'Aprovado',
          value: EQualificationStatus.APPROVED as string,
        },
        ...statusApproval.map((status) => ({
          label: status.description,
          value: status.identifier,
        })),
        {
          label: 'Negado',
          value: EQualificationStatus.DENIED as string,
        },
      ];

      const currentStatus = getApplicationStatusValue(application);

      if (!options.some((option) => option.value === currentStatus)) {
        options.splice(1, 0, {
          label:
            application.status_description ||
            EApplicationStatusDescription[
              EApplicationStatus.PENDING_QUALIFICATION
            ],
          value: currentStatus,
        });
      }

      return options;
    }

    if (isPendingQualification) {
      return [
        {
          label:
            EApplicationStatusDescription[EApplicationStatus.PENDING_RANDOM],
          value: EQualificationStatus.PENDING_RANDOM as string,
        },
        {
          label: EApplicationStatusDescription[EApplicationStatus.CANCELED],
          value: EQualificationStatus.CANCELED as string,
        },
      ];
    }

    return handleGetStaticStatusList(application.status);
  }, []);

  const filterCount = useMemo(() => {
    let counter = 0;
    if (filters.borrower) counter++;
    if (filters.originator) counter++;
    if (filters.minCreatedAtDate || filters.maxCreatedAtDate) counter++;
    if (filters.minDisbursementDate || filters.maxDisbursementDate) counter++;
    if (
      filters.minDisbursementProcessedAtDate ||
      filters.maxDisbursementProcessedAtDate
    )
      counter++;
    if (filters.productId) counter++;
    if (filters.fundingId) counter++;
    if (filters.taxpayerId) counter++;
    if (filters.status?.length !== 6) counter++;
    return counter;
  }, [filters]);

  const onApplyFilters = useCallback((f: TFilters) => {
    setFilters(f);
    setIsOpenFilters(false);
  }, []);

  const handleCopyId = useCallback((id: string) => {
    navigator.clipboard.writeText(id);
    showSnackbarCopy();
  }, []);

  return (
    <BoxSection level="wrapper">
      <ButtonContainer>
        <ApplicationsSearchFilters
          inputValue={searchInputValue}
          filterCount={filterCount}
          onClearInput={() => {
            setCurrentPage(0);
            setLinesPerPage(10);
            setSearchInputValue(() => '');
            setTimeout(() => handleGetApplications(0, linesPerPage, ''));
          }}
          onInputChange={handleSearchInputChange}
          onSelectFilterItem={(selectedFilterValue) => {
            setSelectedFilter(() => selectedFilterValue);
          }}
          selectedFilter={selectedFilter?.name}
          onSearch={handleSearch}
          onOpenFilters={() => setIsOpenFilters(() => true)}
        />
      </ButtonContainer>
      {borrowerStatusOptions &&
      applicationState &&
      applicationState.length > 0 ? (
        <>
          <Container>
            <TableArea>
              <TableWrapper>
                <Table
                  showPagination
                  handlePageChange={(selectedPage) => {
                    setCurrentPage(selectedPage - 1);
                    handleGetApplications(selectedPage - 1, linesPerPage);
                  }}
                  handleLinesPerPage={(selectedLinesPerPage) => {
                    setCurrentPage(0);
                    setLinesPerPage(
                      selectedLinesPerPage === 0 ? 10 : selectedLinesPerPage,
                    );
                  }}
                  overflowVisible={true}
                  totalElementsQty={totalItemsQty}
                  currentPage={currentPage + 1}
                  linesPerPage={linesPerPage}
                  tableBodyContent={applicationState?.map(
                    (application, index) => {
                      const originator = originators?.find(
                        (o) => o.id === application?.originator?.id,
                      );
                      return {
                        id: application?.id || String(index),
                        cells: [
                          {
                            id: '1',
                            content: (
                              <TableBodyCellContainer
                                style={{ maxWidth: '150px', display: 'flex' }}
                              >
                                <TextS
                                  weight="Regular"
                                  style={{
                                    width: '90%',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {application?.id}
                                </TextS>
                                <ButtonPrimary
                                  typeVariant="ghost"
                                  style={{
                                    padding: '0px',
                                    width: 'fit-content',
                                    marginRight: '5px',
                                  }}
                                  onClick={() => handleCopyId(application.id)}
                                >
                                  <CopyIcon
                                    color={
                                      theme.palette.brand.primary.base as string
                                    }
                                  />
                                </ButtonPrimary>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '2',
                            content: (
                              <TableBodyCellContainer>
                                <TextS weight="Regular">
                                  {application.productData
                                    ?.type_product_emission
                                    ? ProductType[
                                        application.productData
                                          .type_product_emission as TypeProductEmission
                                      ]
                                    : '-'}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '3',
                            content: (
                              <TableBodyCellContainer>
                                <TextS>
                                  {application?.sequential_id || '-'}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '4',
                            content: (
                              <TableBodyCellContainer>
                                <TextS>
                                  {(() => {
                                    const formattedDate = formatDateTimePtBr(
                                      application?.created_at,
                                    );
                                    return formattedDate === '-'
                                      ? formattedDate
                                      : formattedDate.replace(' ', ' às ');
                                  })()}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '5',
                            content: (
                              <TableBodyCellContainer>
                                <TextS>{originator?.legal_name || '-'}</TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '6',
                            content: (
                              <TableBodyCellContainer>
                                <TextS>
                                  {application.borrowerDetails?.taxpayer_id
                                    ? normalizeCpfCnpj(
                                        application.borrowerDetails.taxpayer_id,
                                      )
                                    : '-'}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '7',
                            content: (
                              <TableBodyCellContainer>
                                <TextS>
                                  {application.productData?.name || '-'}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '8',
                            content: (
                              <TableBodyCellContainer>
                                <TextS style={{ paddingRight: '5px' }}>
                                  {application?.status ===
                                    EApplicationStatus.ISSUED &&
                                  application?.disbursement?.processed_at
                                    ? formatDateFromBackend(
                                        application?.disbursement?.processed_at,
                                      ).toLocaleDateString('pt-BR')
                                    : '-'}
                                </TextS>
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '9',
                            content: (
                              <TableBodyCellContainer>
                                <CustomTableSelect
                                  info={application.status_description}
                                  selectItems={getStatusList(
                                    application,
                                  ).filter(
                                    handleStatusListPermissions(
                                      application,
                                      getApplicationStatusValue(application),
                                      userInfo,
                                    ),
                                  )}
                                  disabled={
                                    !isProfileAuthorized(
                                      EAccountPermissions.UPDATE_APPLICATION_QUALIFICATION,
                                    ) || !application.productData
                                  }
                                  isLoading={
                                    handleUpdateQualificationStatusLoading ===
                                    application?.id
                                  }
                                  handleChange={(identifier) =>
                                    handleUpdateQualificationStatusAction(
                                      application,
                                      identifier,
                                    )
                                  }
                                  value={getApplicationStatusValue(application)}
                                />
                              </TableBodyCellContainer>
                            ),
                          },
                          {
                            id: '10',
                            content: (
                              <TableBodyCellContainer
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  margin: '0px',
                                }}
                              >
                                <ButtonPrimary
                                  typeVariant="ghost"
                                  style={{ width: 'fit-content' }}
                                  onClick={() =>
                                    handleOpenApplicationDetail(application)
                                  }
                                >
                                  <EyeIcon
                                    color={
                                      theme.palette.brand.primary.base as string
                                    }
                                  />
                                </ButtonPrimary>
                              </TableBodyCellContainer>
                            ),
                          },
                        ].filter((item) => is_issuer_app || item.id !== '5'),
                      };
                    },
                  )}
                  tableHeadCell={tableHeader.filter(
                    (item) => is_issuer_app || item.id !== '5',
                  )}
                />
              </TableWrapper>
            </TableArea>
          </Container>
        </>
      ) : (
        <EmptyContainer>
          {loadings.isApplicationLoading || isLoading ? (
            <Spinner />
          ) : (
            <>
              <img src={EmptyApplications} />
              <Heading4
                style={{
                  marginTop: '30px',
                  color: theme.palette.brand.secondary.base,
                }}
                fontWeight="B"
              >
                {hasActiveSearch
                  ? 'Nenhum resultado encontrado'
                  : 'Nenhum empréstimo solicitado'}
              </Heading4>
            </>
          )}
        </EmptyContainer>
      )}
      <ApplicationDetailsSideSheet
        application={applicationDetail ?? undefined}
        productInfo={applicationDetail?.productData}
        isOpen={applicationDetail !== null}
        handleClose={handleCloseApplicationDetail}
        handleUpdateQualificationStatus={handleUpdateQualificationStatusAction}
      />
      <ApplicationsFilters
        filters={filters}
        handleApply={onApplyFilters}
        handleClose={() => setIsOpenFilters(false)}
        isLoading={loadings.isApplicationLoading}
        hideApplication={true}
        isOpen={isOpenFilters}
      />
    </BoxSection>
  );
};

export default ApplicationContent;
